import React, { useState, useEffect, useCallback, useRef } from "react";
import "./ChatWidget.css";

const ChatWidget = ({ tenantId, tenantSecret }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [threadId, setThreadId] = useState(null);
  const messagesEndRef = useRef(null);
  const [cartNotification, setCartNotification] = useState(null);

  // Function to scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Scroll to bottom when messages update
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Create a new thread with OpenAI
  const createNewThread = useCallback(async () => {
    try {
      const response = await fetch(
        "https://us-central1-woyalty-f284e.cloudfunctions.net/main/api/v1/createthread",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            // "Authorization": `Bearer ${apiKey}`,
          },
          body: JSON.stringify({}),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to create thread");
      }

      const data = await response.json();
      console.log("Thread created with ID:", data.id);
      setThreadId(data.id);
    } catch (error) {
      console.error("Error creating thread:", error);
    }
  }, []);

  // Get product information from WordPress WooCommerce
  const getProductInfo = useCallback(async (productId) => {
    if (!productId) {
      console.error("Missing required parameter: productId");
      return null;
    }

    try {
      // Check if we're in WordPress environment
      const isWordPress = typeof window.chatWidgetConfig !== "undefined";
      if (!isWordPress) {
        console.warn("Not in WordPress environment, using mock data");
        // Return mock data for testing outside WordPress
        return {
          id: productId,
          name: "Test Product",
          description: "This is a test product",
          price: `<span class="woocommerce-Price-amount amount"><bdi><span class="woocommerce-Price-currencySymbol">&#82;&#77;</span>39.00</bdi></span>`,
          image:
            "https://chatbot.woyalty.com/wp-content/uploads/2023/11/product-image-11.webp",
          permalink:
            "https://chatbot.woyalty.com/index.php/product/condimentum-mattis/",
        };
      }

      // In WordPress environment, use our custom REST API endpoint
      const baseUrl =
        window.chatWidgetConfig.api_root ||
        `${window.location.origin}/wp-json/`;
      const response = await fetch(
        `${baseUrl}chat-widget/v1/product/${productId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to get product info for ID: ${productId}`);
      }

      const data = await response.json();
      console.log("Product info:", data);
      return data;
    } catch (error) {
      console.error("Error fetching product info:", error);
      return null;
    }
  }, []);

  // Add product to cart
  const addToCart = useCallback(async (product) => {
    if (!product || !product.id) {
      console.error("Invalid product data");
      return false;
    }

    try {
      // Generate add-to-cart URL using the specific format from the requirement
      const quantity = 1; // Default quantity
      const cartUrl = `https://chatbot.woyalty.com/?add-to-cart=${product.id}&quantity=${quantity}`;

      // Show notification
      setCartNotification({
        productName: product.name,
        success: true,
        timestamp: Date.now(),
      });

      // Redirect to the cart URL
      window.location.href = cartUrl;

      return true;
    } catch (error) {
      console.error("Error adding product to cart:", error);
      setCartNotification({
        productName: product.name,
        success: false,
        timestamp: Date.now(),
      });
      return false;
    }
  }, []);

  // Hide cart notification after 3 seconds
  useEffect(() => {
    if (cartNotification) {
      const timer = setTimeout(() => {
        setCartNotification(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [cartNotification]);

  // Load chat state from sessionStorage on mount
  useEffect(() => {
    const savedState = sessionStorage.getItem("chatState");
    console.log("Loaded saved state:", savedState);
    if (savedState) {
      const {
        isOpen: savedIsOpen,
        messages: savedMessages,
        threadId: savedThreadId,
      } = JSON.parse(savedState);
      setIsOpen(savedIsOpen);
      setMessages(savedMessages);
      setThreadId(savedThreadId);
    } else {
      // Create a new thread if none exists
      console.log("No saved state found, creating a new thread...");
      createNewThread();
    }
  }, [createNewThread]);

  // Save chat state to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem(
      "chatState",
      JSON.stringify({
        isOpen,
        messages,
        threadId,
      })
    );
  }, [isOpen, messages, threadId]);

  // Send message to OpenAI and get response
  const sendToOpenAI = async (userMessage) => {
    if (!threadId) {
      console.error("Missing required parameters: threadId");
      return;
    }

    try {
      // Add user message to thread
      const response = await fetch(
        `https://us-central1-woyalty-f284e.cloudfunctions.net/main/api/v1/processMessage`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            message: userMessage,
            thread_id: threadId,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to process message");
      }
      const data = await response.json();
      console.log("AI response:", data);

      // Parse the JSON
      const jsonObject = JSON.parse(data.message);

      return jsonObject;
    } catch (error) {
      console.error("Error fetching message:", error);
      return "Sorry, I encountered an error processing your request.";
    }
  };

  const formatMessage = (message) => {
    // First remove any citation references
    const withoutCitations = message.replace(/【\d+:\d+†[a-zA-Z]+】/g, "");

    // Replace text between asterisks with span elements with a bold class
    return withoutCitations.replace(
      /\*\*(.+?)\*\*/g,
      '<span class="bold-text">$1</span>'
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (inputMessage.trim()) {
      // Add user message to UI
      const userMessage = inputMessage.trim();
      setMessages([
        ...messages,
        { text: userMessage, sender: "user", timestamp: Date.now() },
      ]);
      setInputMessage("");
      setIsLoading(true);

      try {
        // Send to OpenAI and get response
        const aiResponse = await sendToOpenAI(userMessage);
        console.log("AI response:", aiResponse);
        // Add AI response to messages
        if (aiResponse) {
          // Check if the response contains product IDs to display
          let productDetails = [];
          if (aiResponse.product_ids && aiResponse.product_ids.length > 0) {
            // Fetch details for all product IDs
            const productPromises = aiResponse.product_ids.map((id) =>
              getProductInfo(id)
            );
            // Wait for all product details to be fetched
            productDetails = await Promise.all(productPromises);
            // Filter out any null results
            productDetails = productDetails.filter(
              (product) => product !== null
            );
          }

          setMessages((prevMessages) => [
            ...prevMessages,
            {
              text: formatMessage(aiResponse.message),
              sender: "bot",
              timestamp: Date.now(),
              productDetails: productDetails,
            },
          ]);
        }
      } catch (error) {
        console.error("Error in chat flow:", error);
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            text: "Sorry, I encountered an error. Please try again later.",
            sender: "bot",
            timestamp: Date.now(),
          },
        ]);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Render product information if available
  const renderProductInfo = (msg) => {
    if (!msg.productDetails || msg.productDetails.length === 0) return null;

    return (
      <div className="product-carousel">
        <div className="product-scroll-container">
          {msg.productDetails.map((product, index) => (
            <div key={index} className="product-card">
              <a
                href={product.permalink}
                target="_self"
                rel="noopener noreferrer"
              >
                <h4>{product.name}</h4>
                {product.image && (
                  <img src={product.image} alt={product.name} />
                )}
                <div dangerouslySetInnerHTML={{ __html: product.price }} />
              </a>
              <button
                className="add-to-cart-button"
                onClick={(e) => {
                  e.preventDefault();
                  addToCart(product);
                }}
              >
                Add to Cart
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div
      className={`chat-widget ${isOpen ? "open" : ""} ${
        isExpanded ? "expanded" : ""
      }`}
    >
      <div className="chat-header">
        <div className="header-title" onClick={() => setIsOpen(!isOpen)}>
          Shop Assistant
        </div>
        {isOpen && (
          <div
            className="expand-button"
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
          >
            {isExpanded ? "⟱" : "⟰"}
          </div>
        )}
      </div>
      {cartNotification && (
        <div
          className={`cart-notification ${
            cartNotification.success ? "success" : "error"
          }`}
        >
          {cartNotification.success
            ? `Added ${cartNotification.productName} to cart!`
            : `Failed to add ${cartNotification.productName} to cart.`}
        </div>
      )}
      {isOpen && (
        <div className="chat-container">
          <div className="messages">
            {messages
              .map((msg, index) => (
                <div key={index} className={`message ${msg.sender}`}>
                  {msg.sender === "bot" ? (
                    <div dangerouslySetInnerHTML={{ __html: msg.text }} />
                  ) : (
                    msg.text
                  )}
                </div>
              ))
              .reduce((acc, message, index) => {
                const msg = messages[index];
                acc.push(message);

                // Add a separate message element for product details if available
                if (
                  msg.sender === "bot" &&
                  msg.productDetails &&
                  msg.productDetails.length > 0
                ) {
                  acc.push(
                    <div
                      key={`product-${index}`}
                      className="message bot product-message"
                    >
                      {renderProductInfo(msg)}
                    </div>
                  );
                }

                return acc;
              }, [])}
            {isLoading && (
              <div className="message bot loading">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          <form onSubmit={handleSubmit} className="input-form">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder="Type your message..."
              disabled={isLoading}
            />
            <button type="submit" disabled={isLoading}>
              {isLoading ? "..." : "Send"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default ChatWidget;
